import React, { useEffect, useState } from "react";
import {
  TrendingUp, Eye, Heart, Rocket, Loader2, RefreshCw, BarChart3,
  Mic2, Radio, CalendarClock, Headphones,
  Facebook, Instagram, Youtube, Twitter, MessageCircle, Music2,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Analytics — Station-wide premium dashboard.
 * Aesthetic: deep navy (#0F172A) + slate cards (#1E293B). Four hero gradient
 * KPI cards (Engagement / Reach / Followers / Platform Performance), brand-
 * colored social tiles, smooth SVG growth chart, news-feature breakdowns.
 *
 * Data source: GET /api/analytics/overview (extended with `kpis`, `last_30_days`,
 * `posts_by_platform`).
 */
const COLORS = {
  bg:      "#0F172A",
  card:    "#1E293B",
  border:  "#243049",
  textHi:  "#F1F5F9",
  textMid: "#94A3B8",
};

const PLATFORM_META = {
  facebook:  { color: "#1877F2", label: "Facebook",  Icon: Facebook },
  instagram: { color: "#E1306C", label: "Instagram", Icon: Instagram },
  x:         { color: "#FFFFFF", label: "X",         Icon: Twitter },
  twitter:   { color: "#FFFFFF", label: "X",         Icon: Twitter },
  tiktok:    { color: "#25F4EE", label: "TikTok",    Icon: Music2 },
  youtube:   { color: "#FF0000", label: "YouTube",   Icon: Youtube },
  whatsapp:  { color: "#25D366", label: "WhatsApp",  Icon: MessageCircle },
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [peak, setPeak] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [overview, peakRes] = await Promise.all([
        api.get("/analytics/overview"),
        api.get("/analytics/peak-hours").catch(() => ({ data: null })),
      ]);
      setData(overview.data);
      setPeak(peakRes.data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="p-6 md:p-10 animate-fade-up" data-testid="analytics-page">
        <div className="rounded-2xl p-16 text-center" style={{ background: COLORS.bg, color: COLORS.textMid }}>
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-cyan-300 mb-2" />
          <div className="text-sm">Crunching analytics…</div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const k = data.kpis || {};
  const eng = k.engagement || {};
  const reach = k.reach || {};
  const foll = k.followers || {};
  const perf = k.performance || {};
  const days = data.last_30_days || [];
  const maxGen = Math.max(1, ...days.map((d) => d.generations));
  const maxSched = Math.max(1, ...days.map((d) => d.scheduled));
  const maxStories = Math.max(1, ...days.map((d) => d.stories));
  const yMax = Math.max(maxGen, maxSched, maxStories);

  return (
    <div className="p-6 md:p-10 animate-fade-up max-w-7xl mx-auto" data-testid="analytics-page">
      <div
        className="rounded-2xl p-6 sm:p-8 space-y-6"
        style={{ background: COLORS.bg, color: COLORS.textHi }}
      >
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">// STATION ANALYTICS</div>
            <div className="font-display text-3xl sm:text-4xl font-black mt-1 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-cyan-300" /> Broadcast performance
            </div>
            <p className="text-xs mt-1" style={{ color: COLORS.textMid }}>
              Engagement, reach, audience growth &amp; platform performance — live across the last 30 days.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="analytics-refresh"
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider hover:text-cyan-300 transition disabled:opacity-50"
            style={{ borderColor: COLORS.border, background: COLORS.card, color: COLORS.textMid }}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* 4 Hero Gradient KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroCard
            testid="kpi-engagement"
            gradient="linear-gradient(135deg, #8B5CF6, #A855F7)"
            accent="#8B5CF6"
            icon={Heart}
            eyebrow="Engagement Breakdown"
            big={formatBig(eng.value || 0)}
            sub={eng.sub || ""}
            delta={fmtDelta(eng.delta_pct)}
            deltaTone={tone(eng.delta_pct)}
          />
          <HeroCard
            testid="kpi-reach"
            gradient="linear-gradient(135deg, #0078D4, #0891B2)"
            accent="#0078D4"
            icon={Eye}
            eyebrow="Reach & Impressions"
            big={formatBig(reach.value || 0)}
            sub={reach.sub || ""}
            delta={fmtDelta(reach.delta_pct)}
            deltaTone={tone(reach.delta_pct)}
          />
          <HeroCard
            testid="kpi-followers"
            gradient="linear-gradient(135deg, #10B981, #059669)"
            accent="#10B981"
            icon={TrendingUp}
            eyebrow="Followers Growth"
            big={formatBig(foll.value || 0)}
            sub={foll.sub || ""}
            delta={fmtDelta(foll.delta_pct)}
            deltaTone={tone(foll.delta_pct)}
          />
          <HeroCard
            testid="kpi-performance"
            gradient="linear-gradient(135deg, #FF8A1F, #E67514)"
            accent="#FF8A1F"
            icon={Rocket}
            eyebrow="Platform Performance"
            big={`${perf.value || 0}%`}
            sub={perf.sub || ""}
            delta={perf.delta_pp != null ? `${perf.delta_pp >= 0 ? "+" : ""}${perf.delta_pp}pp` : ""}
            deltaTone={tone(perf.delta_pp)}
          />
        </div>

        {/* Secondary KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <MiniStat icon={Mic2} label="Content" big={data.total_content} sub="generations" />
          <MiniStat icon={Headphones} label="Podcasts" big={data.total_podcasts} sub="episodes" />
          <MiniStat icon={CalendarClock} label="Scheduled" big={data.total_scheduled} sub="posts" />
          <MiniStat icon={Radio} label="Shows" big={data.total_shows} sub="programmed" />
          <MiniStat icon={Headphones} label="DJs" big={data.total_djs} sub="on roster" />
        </div>

        {/* Social platforms (brand colors) */}
        <Section
          title="// SOCIAL PERFORMANCE"
          subtitle={`${(data.posts_by_platform || []).length} platforms`}
        >
          {(data.posts_by_platform || []).length === 0 ? (
            <div className="text-center py-8 text-xs" style={{ color: COLORS.textMid }}>
              <Rocket className="h-5 w-5 text-cyan-300 mx-auto mb-2" />
              Schedule posts via Generator or News Center to start populating channels.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-platforms">
              {(data.posts_by_platform || []).map((p) => {
                const meta = PLATFORM_META[p.platform?.toLowerCase()] || {
                  color: "#94A3B8", label: p.platform, Icon: Rocket,
                };
                const Icon = meta.Icon;
                const displayColor = meta.color === "#FFFFFF" ? COLORS.textHi : meta.color;
                return (
                  <div
                    key={p.platform}
                    data-testid={`analytics-platform-${p.platform}`}
                    className="rounded-xl p-4 border transition hover:scale-[1.03]"
                    style={{
                      background: COLORS.card,
                      borderColor: COLORS.border,
                      boxShadow: `inset 0 0 0 1px ${meta.color}22`,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="h-9 w-9 rounded-lg flex items-center justify-center"
                        style={{ background: `${meta.color}22`, color: meta.color }}
                      ><Icon className="h-4 w-4" /></div>
                      <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: COLORS.textMid }}>posts</span>
                    </div>
                    <div className="mt-3 font-display text-2xl font-black" style={{ color: displayColor }}>{p.count}</div>
                    <div className="text-xs font-semibold" style={{ color: COLORS.textHi }}>{meta.label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Peak Hours — released at peak vs off-peak */}
        {peak && (
          <Section title="// PEAK HOURS" subtitle={`Last ${peak.days} days · released at peak vs off-peak`}>
            {(peak.peak.total + peak.off_peak.total) === 0 ? (
              <div className="text-xs" style={{ color: COLORS.textMid }} data-testid="analytics-peak-empty">
                Nothing published in the last {peak.days} days — once posts go out, you'll see how peak-hour releases compare here.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="analytics-peak-tiles">
                  <PeakTile testid="analytics-peak-tile-peak" label="At peak hours" color="#A78BFA" stats={peak.peak} />
                  <PeakTile testid="analytics-peak-tile-off" label="Off-peak" color="#64748B" stats={peak.off_peak} />
                </div>
                {peak.windows.length > 0 && (
                  <>
                    <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: COLORS.textMid }}>Busiest windows</div>
                    <BarRows rows={peak.windows.map((w) => ({ label: w.label, value: w.count, color: "#A78BFA" }))} />
                  </>
                )}
                {peak.platforms.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="analytics-peak-platforms">
                    {peak.platforms.map((p) => (
                      <div key={p.platform} className="rounded-lg border p-2.5 text-xs"
                        style={{ background: COLORS.bg, borderColor: COLORS.border }}
                        data-testid={`analytics-peak-platform-${p.platform}`}>
                        <div className="font-semibold" style={{ color: COLORS.textHi }}>{PLATFORM_META[p.platform]?.label || p.platform}</div>
                        <div className="font-mono text-[10px] mt-1" style={{ color: COLORS.textMid }}>
                          <span style={{ color: "#A78BFA" }}>{p.peak} peak</span> · {p.off_peak} off-peak
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!peak.enabled && (
                  <div className="mt-3 text-[11px]" style={{ color: COLORS.textMid }} data-testid="analytics-peak-hint">
                    Best Time Posting is off — turn it on in Now Playing → Rules &amp; Setup to release posts at these windows automatically.
                  </div>
                )}
              </>
            )}
          </Section>
        )}

        {/* Growth Chart */}
        <Section
          title="// GROWTH CHART"
          subtitle="Last 30 days · content · scheduled · news"
        >
          <SmoothGrowthChart days={days} yMax={yMax} />
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px]" style={{ color: COLORS.textMid }}>
            <Legend color="#8B5CF6" label="Engagement (scheduled)" />
            <Legend color="#0078D4" label="Reach (content)" />
            <Legend color="#10B981" label="News stories" />
          </div>
        </Section>

        {/* Content by platform — bars */}
        <Section title="// CONTENT BY PLATFORM" subtitle="Lifetime distribution">
          {(data.by_platform || []).length === 0 ? (
            <div className="text-xs" style={{ color: COLORS.textMid }}>No platform data yet — generate content first.</div>
          ) : (
            <BarRows rows={(data.by_platform || []).map((p) => {
              const meta = PLATFORM_META[p.platform?.toLowerCase()] || { color: "#0093FF", label: p.platform };
              return { label: meta.label, value: p.count, color: meta.color === "#FFFFFF" ? "#94A3B8" : meta.color };
            })} />
          )}
        </Section>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
function HeroCard({ testid, gradient, accent, icon: Icon, eyebrow, big, sub, delta, deltaTone = "neutral" }) {
  const deltaColor = deltaTone === "positive" ? "#22C55E"
                    : deltaTone === "negative" ? "#EF4444"
                    : "#94A3B8";
  return (
    <div
      data-testid={testid}
      className="relative rounded-2xl p-5 overflow-hidden transition hover:scale-[1.02] cursor-default"
      style={{
        background: COLORS.card,
        borderColor: COLORS.border,
        boxShadow: `0 8px 32px -8px ${accent}33, inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}
    >
      <div
        className="absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-25 blur-2xl pointer-events-none"
        style={{ background: gradient }}
      />
      <div className="relative">
        <div className="flex items-start justify-between">
          <div
            className="h-11 w-11 rounded-xl flex items-center justify-center shadow-lg"
            style={{ background: gradient, boxShadow: `0 6px 18px -4px ${accent}80` }}
          ><Icon className="h-5 w-5 text-white" /></div>
          {delta && (
            <span
              className="text-[11px] font-mono px-2 py-1 rounded-md"
              style={{
                color: deltaColor,
                background: `${deltaColor}1a`,
                border: `1px solid ${deltaColor}33`,
              }}
            >{delta}</span>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-4" style={{ color: COLORS.textMid }}>
          {eyebrow}
        </div>
        <div className="font-display text-3xl sm:text-4xl font-black mt-1" style={{ color: COLORS.textHi }}>
          {big}
        </div>
        <div className="text-[11px] mt-1" style={{ color: COLORS.textMid }}>{sub}</div>
      </div>
    </div>
  );
}

function PeakTile({ testid, label, color, stats }) {
  return (
    <div data-testid={testid} className="rounded-xl p-4 border"
      style={{ background: COLORS.bg, borderColor: COLORS.border, boxShadow: `inset 0 0 0 1px ${color}22` }}>
      <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color }}>{label}</div>
      <div className="font-display text-3xl font-black mt-1" style={{ color: COLORS.textHi }}>{stats.total}</div>
      <div className="text-[11px] mt-0.5" style={{ color: COLORS.textMid }}>
        {stats.posted} posted · {stats.failed} failed{stats.success_rate != null ? ` · ${stats.success_rate}% success` : ""}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, big, sub }) {  return (
    <div className="rounded-xl p-4 border" style={{ background: COLORS.card, borderColor: COLORS.border }}>
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider" style={{ color: COLORS.textMid }}>
        <Icon className="h-3.5 w-3.5 text-cyan-300" /> {label}
      </div>
      <div className="font-display text-2xl font-black mt-1" style={{ color: COLORS.textHi }}>{big}</div>
      <div className="text-[10px] mt-0.5" style={{ color: COLORS.textMid }}>{sub}</div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="rounded-2xl p-5 border" style={{ background: COLORS.card, borderColor: COLORS.border }}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">{title}</div>
        {subtitle && <div className="text-[11px]" style={{ color: COLORS.textMid }}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-3 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}

function SmoothGrowthChart({ days, yMax }) {
  const W = 1000, H = 240, pad = 16;
  const n = days.length;
  if (n === 0) return <div className="text-xs" style={{ color: COLORS.textMid }}>No data yet.</div>;
  const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, n - 1);
  const y = (v) => H - pad - (v / Math.max(1, yMax)) * (H - 2 * pad);
  const smooth = (key) => {
    const pts = days.map((d, i) => [x(i), y(d[key] || 0)]);
    let p = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(i + 2, pts.length - 1)];
      const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
      const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
      p += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
    }
    return p;
  };
  const reachPath = smooth("generations");
  const engPath   = smooth("scheduled");
  const storyPath = smooth("stories");
  const reachArea = reachPath + ` L ${x(n - 1)} ${H - pad} L ${x(0)} ${H - pad} Z`;
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-56 md:h-64" data-testid="analytics-growth-chart">
        <defs>
          <linearGradient id="reachGradAna" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0078D4" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#0078D4" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((p) => (
          <line key={p} x1={pad} x2={W - pad}
                y1={H - pad - p * (H - 2 * pad)} y2={H - pad - p * (H - 2 * pad)}
                stroke="#243049" strokeDasharray="4 4" />
        ))}
        <path d={reachArea} fill="url(#reachGradAna)" />
        <path d={reachPath} fill="none" stroke="#0078D4" strokeWidth="2.5" />
        <path d={engPath}   fill="none" stroke="#8B5CF6" strokeWidth="2.5" />
        <path d={storyPath} fill="none" stroke="#10B981" strokeWidth="2.5" strokeDasharray="4 3" />
      </svg>
      <div className="flex justify-between text-[9px] font-mono mt-1" style={{ color: COLORS.textMid }}>
        <span>{days[0]?.date}</span>
        <span>{days[Math.floor(days.length / 2)]?.date}</span>
        <span>{days[days.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function BarRows({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label} className="text-xs">
          <div className="flex items-center justify-between">
            <span style={{ color: COLORS.textHi }}>{r.label}</span>
            <span className="font-mono" style={{ color: r.color }}>{r.value}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "#0F172A" }}>
            <div className="h-full rounded-full transition-all duration-700"
                 style={{ width: `${(r.value / max) * 100}%`, background: r.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatBig(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + "M";
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(".0", "") + "K";
  return n.toLocaleString();
}

function fmtDelta(pct) {
  if (pct == null) return "";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct}%`;
}

function tone(delta) {
  if (delta == null) return "neutral";
  if (delta > 0) return "positive";
  if (delta < 0) return "negative";
  return "neutral";
}
